import React, { useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { Loading } from '@components/atoms/Loading';
import { AssistantConversation } from '@models/assistant.model';
import { useTranslation } from '@hooks/useTranslation';

interface ConversationListProps {
  conversations: AssistantConversation[];
  activeId?: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}

/**
 * Sprint 37, story-196. The Assistant page's left rail: "New chat", then the caller's
 * conversations most recent first (the backend orders by last activity). Each row shows the
 * (inferred) title, plus the project name as a muted subtitle when the conversation is bound to a
 * project. Titles are rendered as plain text, never Markdown.
 */
export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeId,
  loading,
  error,
  onRetry,
  onSelect,
  onNew,
  onDelete,
}) => {
  const t = useTranslation('assistant');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | undefined>();

  return (
    <div
      className="flex flex-col h-full min-h-0 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
      data-testid="assistant-conversation-list">
      <button
        type="button"
        onClick={onNew}
        className="flex items-center gap-2 px-3 py-3 text-sm font-medium text-primary hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
        data-testid="assistant-new-chat">
        <AddIcon sx={{ fontSize: 16 }} />
        {t.newChat}
      </button>

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
        {loading && (
          <div className="p-4 flex justify-center">
            <Loading size="sm" />
          </div>
        )}
        {error && (
          <div className="p-3 text-xs text-red-600" data-testid="assistant-list-error">
            {t.listError}{' '}
            {onRetry && (
              <button type="button" className="underline" onClick={onRetry}>
                {t.retry}
              </button>
            )}
          </div>
        )}
        {!loading && !error && conversations.length === 0 && (
          <div className="p-3 text-xs text-gray-500" data-testid="assistant-list-empty">
            {t.listEmpty}
          </div>
        )}
        {conversations.map((c) => {
          const active = c.conversationId === activeId;
          const title = c.title || t.untitled;
          return (
            <div
              key={c.conversationId}
              role="button"
              tabIndex={0}
              aria-current={active ? 'page' : undefined}
              title={title}
              className={`flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 ${
                active ? 'border-primary bg-primary/5' : 'border-transparent'
              }`}
              onClick={() => onSelect(c.conversationId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(c.conversationId);
                }
              }}
              data-testid="assistant-conversation-row">
              <div className="min-w-0">
                <div className={`truncate text-sm ${active ? 'font-semibold' : ''}`}>{title}</div>
                {c.projectName && (
                  <div className="truncate text-xs text-gray-500" data-testid="assistant-conversation-project">
                    {c.projectName}
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label={t.deleteConversationTitle}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDeleteId(c.conversationId);
                }}
                className="text-gray-400 hover:text-red-500 flex-shrink-0">
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          );
        })}
      </div>

      <ConfirmationModal
        isOpen={!!pendingDeleteId}
        title={t.deleteConversationTitle}
        description={t.deleteConversationBody}
        onClose={() => setPendingDeleteId(undefined)}
        onConfirm={() => {
          if (pendingDeleteId) onDelete(pendingDeleteId);
          setPendingDeleteId(undefined);
        }}
      />
    </div>
  );
};

export default ConversationList;
